import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import { COUNTRY_OPTIONS } from 'src/views/uaempci/Common/constants/country'
import { CANADA_STATE_CODES } from './canadaStateCodes'

const ACTION_CODE_OPTIONS = [
  { value: 'A', text: 'A - Add' },
  { value: 'U', text: 'U - Update Postal' },
]

const blankDraft = () => ({
  abiTransId: null,
  manufacturerId: '',
  name: '',
  country: '',
  state: '',
  street: '',
  city: '',
  postalCode: '',
  actionCode: '',
})

const validateDraft = (draft, isCreate) => {
  const errors = {}
  if (!String(draft.name ?? '').trim()) errors.name = 'Name is required'
  if (!draft.country) errors.country = 'Country is required'
  if (draft.country === 'CA' && !draft.state) errors.state = 'State is required'
  if (!String(draft.street ?? '').trim()) errors.street = 'Street is required'
  if (!String(draft.city ?? '').trim()) errors.city = 'City is required'
  if (!String(draft.postalCode ?? '').trim()) errors.postalCode = 'Postal Code is required'
  if (!isCreate && !draft.actionCode) errors.actionCode = 'Action Code is required'
  return errors
}

export const ManufacturerMidDialog = ({ open, mode, initialValue, onClose, onSave }) => {
  const [draft, setDraft] = useState(blankDraft)
  const [errors, setErrors] = useState({})

  const isCreate = mode === 'create'

  useEffect(() => {
    if (open) {
      setDraft(initialValue ? { ...blankDraft(), ...initialValue } : blankDraft())
      setErrors({})
    }
  }, [open, initialValue])

  const handleFieldChange = (name, value) => {
    setDraft((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && value !== 'CA' ? { state: '' } : {}),
    }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const handleSave = () => {
    const nextErrors = validateDraft(draft, isCreate)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    onSave(draft)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{isCreate ? 'Add Manufacturer' : 'Edit Manufacturer'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>

          <TextField
            label='Name'
            fullWidth
            required
            value={draft.name}
            error={!!errors.name}
            helperText={errors.name}
            onChange={(event) => handleFieldChange('name', event.target.value)}
          />

          <TextField
            select
            label='Country'
            fullWidth
            required
            value={draft.country}
            error={!!errors.country}
            helperText={errors.country}
            onChange={(event) => handleFieldChange('country', event.target.value)}
          >
            {COUNTRY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.text}</MenuItem>
            ))}
          </TextField>

          {draft.country === 'CA' && (
            <TextField
              select
              label='State'
              fullWidth
              required
              value={draft.state}
              error={!!errors.state}
              helperText={errors.state}
              onChange={(event) => handleFieldChange('state', event.target.value)}
            >
              {CANADA_STATE_CODES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.text}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label='Street'
            fullWidth
            required
            value={draft.street}
            error={!!errors.street}
            helperText={errors.street}
            onChange={(event) => handleFieldChange('street', event.target.value)}
          />

          <TextField
            label='City'
            fullWidth
            required
            value={draft.city}
            error={!!errors.city}
            helperText={errors.city}
            onChange={(event) => handleFieldChange('city', event.target.value)}
          />

          <TextField
            label='Postal Code'
            fullWidth
            required
            value={draft.postalCode}
            error={!!errors.postalCode}
            helperText={errors.postalCode}
            onChange={(event) => handleFieldChange('postalCode', event.target.value)}
          />

          {!isCreate && (
            <TextField
              label='Manufacturer ID'
              fullWidth
              disabled
              value={draft.manufacturerId ?? ''}
            />
          )}

          {!isCreate && (
            <TextField
              select
              label='Action Code'
              fullWidth
              required
              value={draft.actionCode}
              error={!!errors.actionCode}
              helperText={errors.actionCode}
              onChange={(event) => handleFieldChange('actionCode', event.target.value)}
            >
              {ACTION_CODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.text}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 1 }}>
        <Buttons
          advance={
            <Button variant='contained' onClick={handleSave} title={isCreate ? 'Add this manufacturer' : 'Save changes'}>
              {isCreate ? 'Add' : 'Save'}
            </Button>
          }
          other={
            <Button variant='outlined' onClick={onClose} title='Go back'>
              Cancel
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  )
}

export default ManufacturerMidDialog